/**
 * Permission management utility for Vulture
 * 
 * This utility provides functions to check if a user has specific permissions
 * based on the response from broncoSDK.getUserPermissions()
 * 
 * Example API response:
 * {
 *   "role": "internal_admin",
 *   "permissions": [
 *     "tools_access",
 *     "user_management",
 *     "csv_upload",
 *     "settings_access",
 *     "programming",
 *     "property_access",
 *     "account_management",
 *     "pop_analytics"
 *   ],
 *   "access_limits": {
 *     "account_limited": false,
 *     "property_limited": false
 *   },
 *   "accessible_data": {
 *     "accounts": [
 *       "829a6946-828c-11eb-ae1a-0d442d14e026",
 *       "d8d22236-2426-46a4-a81d-6944886a54b8"
 *     ]
 *   },
 *   "component_access": [
 *     {
 *       "feature": "enable_brood_proof_of_play"
 *     }
 *   ]
 * }
 */

// Store the permissions data from the API
let permissionsData = null;

/**
 * Initialize the permissions with data from the API
 * @param {Object} data - Permissions data from broncoSDK.getUserPermissions()
 */
const initPermissions = (data) => {
  if (!data) {
    console.warn('Attempted to initialize permissions with empty data');
    return;
  }
  
  permissionsData = data;
};

/**
 * Check if the user has a specific permission
 * @param {string} permission - The permission key to check
 * @returns {boolean} - Whether the user has the specified permission
 */
const hasPermission = (permission) => {
  if (!permissionsData || !permissionsData.permissions) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return false;
  }

  return permissionsData.permissions.includes(permission);
};

/**
 * Check if the user has any of the specified permissions
 * @param {Array<string>} permissions - Array of permission keys to check
 * @returns {boolean} - Whether the user has any of the specified permissions
 */
const hasAnyPermission = (permissions) => {
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Check if the user has all of the specified permissions
 * @param {Array<string>} permissions - Array of permission keys to check
 * @returns {boolean} - Whether the user has all of the specified permissions
 */
const hasAllPermissions = (permissions) => {
  return permissions.every(permission => hasPermission(permission));
};

/**
 * Check if the user has access to a specific account
 * @param {string} accountUUID - Account UUID to check
 * @returns {boolean} - Whether the user has access to the account
 */
const hasAccountAccess = (accountUUID) => {
  if (!permissionsData) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return false;
  }

  // If account access is not limited, user has access to all accounts
  if (!permissionsData.access_limits.account_limited) {
    return true;
  }

  // Otherwise, check if the account is in the accessible_data.accounts array
  return permissionsData.accessible_data && 
         permissionsData.accessible_data.accounts && 
         permissionsData.accessible_data.accounts.includes(accountUUID);
};

/**
 * Check if property access is limited for the user
 * @returns {boolean} - Whether property access is limited
 */
const isPropertyAccessLimited = () => {
  if (!permissionsData || !permissionsData.access_limits) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return true; // Default to limited access for safety
  }
  
  return permissionsData.access_limits.property_limited;
};

/**
 * Check if the user has a specific component/feature access
 * @param {string} featureName - The feature name to check
 * @returns {boolean} - Whether the user has access to the specified feature
 */
const hasFeatureAccess = (featureName) => {
  if (!permissionsData || !permissionsData.component_access) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return false;
  }

  return permissionsData.component_access.some(component => 
    component.feature === featureName
  );
};

/**
 * Scope predicates — the frontend mirror of PermissionContext's on the bronco side.
 * Gate on these, never on getUserRole(): a role's name says nothing about its reach.
 *
 * Independent predicates rather than one accessor because a role may hold several narrow
 * scopes at once (content_manager carries both scope:slideshow and scope:folder).
 */
const hasGlobalScope = () => hasPermission('scope:global');

const isPropertyScoped = () => hasPermission('scope:property');

const isSlideshowScoped = () => hasPermission('scope:slideshow');

const isFolderScoped = () => hasPermission('scope:folder');

/**
 * Whether the user is narrowed BELOW account level (property-, slideshow- or folder-scoped).
 * The complement of "sees a whole account or the whole fleet".
 * @returns {boolean}
 */
const isNarrowScoped = () => !hasGlobalScope() && !hasPermission('scope:account');

/**
 * Rank of a scope by reach. Mirrors scopePermRank in bronco's permission_context.go.
 * @param {string} scopeType - "global" | "account" | "property" | "slideshow" | "folder"
 * @returns {number}
 */
const scopeRank = (scopeType) => {
  switch (scopeType) {
    case 'global':
      return 3;
    case 'account':
      return 2;
    case 'property':
    case 'slideshow':
    case 'folder':
      return 1;
    default:
      return 0;
  }
};

/**
 * Whether the current user may administer a user whose scope is targetScopeType: you may
 * only manage users whose reach is strictly NARROWER than your own. Takes a scope, never a
 * role name. A missing target scope fails closed.
 *
 * The backend's full rule (model/user_reach.go) needs the target's permission set, which
 * list payloads don't carry — they carry its verdict as `beyond_reach` instead. Pass it
 * when the payload has it: a global caller may administer any non-global user, but NOT a
 * fellow global user whose permissions exceed their own (internal_user must not open
 * internal_admin — the backend 403s it, so the UI must not offer it).
 *
 * @param {string} targetScopeType - the other user's scope_type, from the user payload
 * @param {boolean} [targetBeyondReach] - the user payload's beyond_reach, when present
 * @returns {boolean}
 */
const canAdministerScope = (targetScopeType, targetBeyondReach) => {
  if (targetBeyondReach === true) {
    return false;
  }

  if (hasGlobalScope()) {
    return true;
  }

  if (!targetScopeType) {
    return false;
  }

  return scopeRank(targetScopeType) < scopeRank(getUserScopeType());
};

/**
 * The current user's own UNCLAMPED scope, derived from their scope:* permission.
 * @returns {string}
 */
const getUserScopeType = () => {
  if (hasGlobalScope()) {
    return 'global';
  }
  if (hasPermission('scope:account')) {
    return 'account';
  }
  if (hasPermission('scope:property')) {
    return 'property';
  }
  if (hasPermission('scope:slideshow')) {
    return 'slideshow';
  }
  if (hasPermission('scope:folder')) {
    return 'folder';
  }

  return 'account';
};

/**
 * Get the user's role
 * @returns {string|null} - The user's role or null if not initialized
 */
const getUserRole = () => {
  if (!permissionsData) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return null;
  }
  
  return permissionsData.role;
};

/**
 * Get all accessible accounts for the user
 * @returns {Array<string>} - Array of accessible account UUIDs
 */
const getAccessibleAccounts = () => {
  if (!permissionsData || !permissionsData.accessible_data || !permissionsData.accessible_data.accounts) {
    return [];
  }
  
  return [...permissionsData.accessible_data.accounts];
};

/**
 * Check if permissions have been initialized
 * @returns {boolean} - Whether permissions have been initialized
 */
const isInitialized = () => {
  return permissionsData !== null;
};

/**
 * Get the raw permissions data
 * @returns {Object|null} - Raw permissions data or null if not initialized
 */
const getRawPermissions = () => {
  return permissionsData;
};

export {
  initPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasAccountAccess,
  isPropertyAccessLimited,
  hasFeatureAccess,
  hasGlobalScope,
  isPropertyScoped,
  isSlideshowScoped,
  isFolderScoped,
  isNarrowScoped,
  getUserScopeType,
  canAdministerScope,
  getUserRole,
  getAccessibleAccounts,
  isInitialized,
  getRawPermissions,
};
